import React from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";

/**
 * PermissionGuard — wraps a route/component and enforces permission checks.
 *
 * Props:
 *   permission  {string}  - Required permission key (e.g. 'orders.view')
 *   children    {node}    - The protected component(s) to render
 *   fallback    {string}  - Redirect path when denied (default: '/access-denied')
 *   superAdminOnly {bool} - If true, only super_admin may access
 *
 * Super admins bypass ALL permission checks.
 * Non-role users (no roleRef) are denied unless super_admin.
 */
const PermissionGuard = ({
  permission,
  children,
  fallback = "/access-denied",
  superAdminOnly = false,
}) => {
  const auth = useSelector((state) => state.auth);

  // Super admin bypasses everything
  if (auth?.userRole === "super_admin") {
    return <>{children}</>;
  }

  // If only super_admin is allowed
  if (superAdminOnly) {
    return <Navigate to={fallback} replace />;
  }

  // If no permission required, just render
  if (!permission) {
    return <>{children}</>;
  }

  // Check if the user's permissions include the required one
  const hasPermission = Array.isArray(auth?.permissions) && auth.permissions.includes(permission);

  if (!hasPermission) {
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
};

export default PermissionGuard;
